import assign from "lodash/assign";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import find from "lodash/find";
import every from "lodash/every";
import findIndex from "lodash/findIndex";
import isObject from "lodash/isObject";
import isFunction from "lodash/isFunction";
import ReactDOM from "react-dom";
import locale from "js/main/locale";
import log from "js/main/log";
import { mapErrors, classnames } from "js/libs/util";
import Markdown from "js/views/widgets/Markdown";
import Tooltip from "js/views/widgets/Tooltip";
import { ReactElement } from "react";

export type Options = string[];
export type OptionsLongForm = Array<{ value: string; display: string }>;

interface BaseItem {
  ref: string;
  // alternative label instead of ref
  label?: string;
  // whether to render label as markdown
  markdownLabel?: boolean;
  // show input addon
  addon?: string;
  // tooltip to show on hover
  tooltip?: string;
  // optional conversion function after validation
  convert?: (src: string) => any;
  // input placeholder text or ref
  placeholder?: boolean | string;
  // custom validation function
  validate?: (value: string) => [boolean, string];

  // whether to render as optional
  optional?: boolean;

  // render state
  rendered?: boolean;
  highlight?: boolean;
  error?: string;
  visited?: boolean;
  validated?: boolean;
  valid?: boolean;

  // depending on field type
  type?: any;
  rows?: any;
  check?: any;
  options?: OptionsLongForm | Options;
  customOptions?: boolean;
}

interface TextAreaItem extends BaseItem {
  value?: string;
  rows?: number;
}

interface InputItem extends BaseItem {
  value?: string;
  // input type
  type?: string;
}

interface CheckItem extends BaseItem {
  check?: boolean;
  value?: boolean;
}

interface SelectItem extends BaseItem {
  value?: string;
  options: OptionsLongForm;
  customOptions?: boolean;
}

export type FormItem = InputItem | CheckItem | TextAreaItem | SelectItem;
export type Form = FormItem[];

const isTextAreaItem = (item: FormItem): item is TextAreaItem => {
  return item.rows !== undefined;
};

const isCheckItem = (item: FormItem): item is CheckItem => {
  return item.check !== undefined;
};

const isSelectItem = (item: FormItem): item is SelectItem => {
  return item.options !== undefined;
};

const isLongFormOptions = (
  options: Options | OptionsLongForm
): options is OptionsLongForm => {
  return typeof options[0] == "object";
};

export interface FormMixinState {
  form?: Form;
  errors?: string[];
  formDisabled?: boolean;
  validated?: boolean;
  valid?: boolean;
}

// prefill formItem values from store
export const prefillForm = function (items: Form, store: Backbone.Model) {
  items.forEach(function (item) {
    const storeValue = store.attributes[item.ref];
    if (storeValue === undefined || storeValue === "") {
      item.value = "";
      return;
    }

    if (item.options) {
      // options in long form
      if (isLongFormOptions(item.options)) {
        let index = findIndex(item.options, { value: storeValue });
        item.value = index == -1 ? "" : storeValue;
        // options in short form
      } else {
        let index = item.options.indexOf(storeValue);
        item.value = index == -1 ? "" : String(index);
      }
    } else if (item.check) {
      item.value = !!storeValue;
    } else {
      item.value = storeValue;
    }

    // conversion functions expect string
    if (item.convert) {
      item.value = String(item.value);
    }
  });
};

// FormMixin: handles creation and validation of forms
export default {
  /* ============ Initialization ========== */

  // build full fledged form array
  _createForm: function (array: Form): Form {
    if (!array.length) {
      return [];
    }

    let baseItem = {
      optional: false,
      value: "",
      rendered: false,
      valid: false,
      validated: false,
    };

    let form = array.map(function (formField) {
      let item: FormItem;
      if (isObject(formField)) {
        item = assign({}, baseItem, formField);
      } else {
        item = assign({}, baseItem, { ref: formField });
      }

      // transform options into long form
      if (item.options) {
        if (typeof item.options[0] != "object") {
          item.options = item.options.map(function (display, index: Number) {
            return { value: String(index), display: display };
          });
        } else {
          item.customOptions = true;
        }
      }
      return item;
    });

    return form;
  },
  fillForm: function (store) {
    let form = this.state.form;
    form.forEach(function (item: FormItem) {
      const storeValue = store.attributes[item.ref];
      if (storeValue === undefined || storeValue === "") {
        item.value = "";
        return;
      }

      if (isSelectItem(item)) {
        if (item.customOptions) {
          let index = findIndex(item.options, { value: storeValue });
          item.value = index == -1 ? "" : storeValue;
        } else {
          let index = findIndex(item.options, { display: storeValue });
          item.value = index == -1 ? "" : String(index);
        }
      } else {
        item.value = storeValue;
      }
    });
    this.setState({ form });
  },

  // Form event listeners
  _handleFocus: function (event) {
    // check if current item is last to be filled
    if (event.target.name) {
      let current = this.getItem(event.target.name);
      let isLast = true;
      const form = this.state.form;
      for (let i = 0; i < form.length; i++) {
        let item = form[i];
        if (
          item.rendered &&
          !item.visited &&
          !item.optional &&
          item !== current
        ) {
          isLast = false;
          break;
        }
      }

      if (isLast) this.setState({ lastItem: current });
    }
  },
  _handleLeave: function (event) {
    // only validate on changes
    if (!this.state.validated) {
      this._validateWrapper();
    }

    // if last focused item is required but hasnt been filled, highlight it
    if (event.target.name) {
      let item = this.getItem(event.target.name);
      if (!item) return;

      item.visited = true;
      const valid = this._validateItem(item);
      if (!valid) item.highlight = true;

      if (isFunction(this.handleFormLeave)) this.handleFormLeave(item, event);

      this.setItem(item);
    }
  },
  _handleChange: function (item: FormItem, event) {
    if (this.state.formDisabled) return;

    if (item.check) {
      item.value = event.target.checked;
    } else {
      item.value = event.target.value;
    }
    item.highlight = false;
    item.validated = false;

    if (isFunction(this.handleFormChange))
      this.handleFormChange(item, event.target.value);

    // if youve already been at this item or if it is
    // the last item in the form validate on typing
    // if(item.visited || item === this.state.lastItem)
    this._validateWrapper();

    this.setItem(item, { validated: false });
  },

  // Component Lifecycle
  getInitialState: function () {
    this.validationCallbacks = [];
    const form: Form = this.getForm ? this._createForm(this.getForm()) : [];
    return {
      form: form,
      formInitialized: !!form.length,
      valid: false,
      validated: false,
    };
  },
  componentWillMount: function () {
    this._validateWrapper = this._getValidationWrapper();
  },
  componentDidMount: function () {
    this._formMounted = true;
    const form = this.refs.form;
    if (!form) {
      throw new Error('You must provide a ref "form".');
    }
    const formComponent = ReactDOM.findDOMNode(form);
    if (!formComponent) {
      throw new Error("Could not find form component.");
    }
    formComponent.addEventListener("blur", this._handleLeave, true);
    formComponent.addEventListener("focus", this._handleFocus, true);
  },
  componentWillUnmount: function () {
    this._formMounted = false;
    const formComponent = ReactDOM.findDOMNode(this.refs.form);
    if (formComponent) {
      formComponent.removeEventListener("blur", this._handleLeave, true);
      formComponent.removeEventListener("focus", this._handleFocus, true);
    }
    log("Unmount form");
  },
  // delayed form init
  setForm: function (form, validateAll = false) {
    this.setState({ form: this._createForm(form) });
    this._validateWrapper(validateAll);
  },

  /* ============= Validation ============= */

  // determines if all required form fields are filled
  isComplete: function () {
    return every(this.state.form, (item) => {
      const itemFilled = item.check ? !!item.value : item.value !== "";
      if (item.check)
        log("complete", !!item.value, item.optional || itemFilled);
      return item.optional || itemFilled;
    });
  },

  _isEmpty: function (item: FormItem, value: any) {
    if (isCheckItem(item)) {
      return !value;
    } else {
      return value === "" || value === null || typeof value === "undefined";
    }
  },

  _validateItem: function (item: FormItem): {
    valid: boolean;
    errorKey?: string;
  } {
    let errorKey: string | undefined;
    item.valid = !!item.valid;
    if (!item.validated) {
      const value = this.getValue(item);
      const isEmpty = this._isEmpty(item, value);

      if (isEmpty && item.optional) {
        item.valid = true;

        // custom validation
      } else if ("validate" in item && isFunction(item.validate)) {
        const validationResult = item.validate(value);

        if (Array.isArray(validationResult)) {
          [item.valid, errorKey] = validationResult;
        } else {
          item.valid = validationResult;
        }
        // optional attribute
      } else {
        item.valid = !isEmpty;
        if (!item.valid) {
          errorKey = `${item.ref}.missing`;
        }
      }
      item.validated = true;
    }

    return { valid: item.valid, errorKey };
  },

  _defaultValidation: function (validateAll: boolean = false): {
    valid: boolean;
    errorKeys: Array<string>;
  } {
    console.log("default validation", validateAll);
    const errorKeys: string[] = [];
    let valid = true;

    this.state.form.forEach((item: FormItem) => {
      const validationResult = this._validateItem(item);
      if (validateAll) item.visited = true;

      if (!validationResult.valid) {
        valid = false;
        if (validationResult.errorKey) {
          errorKeys.push(validationResult.errorKey);
        }
        if (validateAll || item.visited) {
        }
      }
    });

    return { valid, errorKeys };
  },

  // Wrap validation to provide a sensible default
  _getValidationWrapper: function () {
    return debounce(
      throttle(function (validateAll = false) {
        this.validatedAll = validateAll;

        // without validation function check form for completion only
        if (
          !Object.hasOwnProperty.call(this, "validate") &&
          !isFunction(this.validate)
        ) {
          const { valid, errorKeys } = this._defaultValidation(validateAll);
          log(
            `default validation validateAll=${validateAll} valid=${valid} errorKeys=${errorKeys}`,
            this.state
          );
          this.handleFormValidation(errorKeys, valid);

          // call supplied validation function
        } else {
          this.validate(this.handleFormValidation);
        }
      }, 500),
      200
    );
  },

  // process errors from validation
  handleFormValidation: function (errorKeys, valid) {
    if (!this._formMounted || !this.refs.form) return;

    // highlight referenced form fields
    let form = this.state.form;
    let refs = errorKeys.map((key) => key.split(".")[0]);
    form.forEach((item) => {
      if (refs.indexOf(item.ref.toLowerCase()) !== -1) item.highlight = true;
    });

    // call and clear validationCallbacks
    this.validationCallbacks.forEach((cb) => cb(valid));
    this.validationCallbacks = [];

    this.setState({ validated: true, valid: valid, form });
    if (
      Object.hasOwnProperty.call(this, "handleErrors") &&
      isFunction(this.handleErrors)
    ) {
      this.handleErrors(mapErrors(errorKeys));
    } else if (isFunction(this.props.onErrors)) {
      this.props.onErrors(mapErrors(errorKeys));
    }
  },

  // add callback to next validation if it succeedes
  validated: function (callback: (valid: boolean) => void) {
    if (this.state.validated && !this.validatedAll) {
      if (callback) callback(this.state.valid);
    } else {
      if (callback) this.validationCallbacks.push(callback);
      this._validateWrapper(true);
    }
  },

  // validates and highlights all missing entries
  highlightAll: function () {
    // log('highlightAll');
    this.validated();
  },

  /* ============= outside handlers / value abstraction ============= */

  // get item by ref
  getItem: function (ref: string): FormItem {
    return find(this.state.form, { ref: ref });
  },

  // clear item value by ref
  clearItem: function (ref) {
    let item = this.getItem(ref);
    if (item) {
      item.value = "";
      this.setItem(item);
    }
  },

  // set item properties and update state
  setItem: function (item, extraState) {
    let form = this.state.form;
    let index = findIndex(form, { ref: item.ref });
    form[index] = item;
    this.setState(assign({ form }, extraState));
  },

  setValue: function (ref, value) {
    let item = this.getItem(ref);
    item.value = value;
    item.validated = false;
    item.visited = true;
    this.setItem(item, { validated: false });
  },

  getValue: function (item: FormItem) {
    let value: string | boolean | number | undefined;
    if (isSelectItem(item)) {
      if (item.customOptions) {
        value = item.value;
      } else if (item.value === "") {
        value = "";
      } else {
        value = find(item.options, { value: item.value })?.display;
      }
    } else if (isCheckItem(item)) {
      return !!item.value;
    } else {
      value = item.value;
    }

    if (item.convert && value !== undefined) value = item.convert(value);

    return value;
  },

  getFormValues: function () {
    let values = {};
    this.state.form.forEach(
      (item: FormItem) => (values[item.ref] = this.getValue(item))
    );
    return values;
  },

  // convenience method
  getValueByRef: function (ref: string) {
    const item = this.getItem(ref);
    if (item) return this.getValue(item);
  },

  // get item Display Text
  getDisplay: function (item: FormItem): string | null {
    if (isSelectItem(item)) {
      let option = find(item.options, { value: item.value });
      return option ? option.display : null;
    } else if (item.type === "password") {
      return null;
    } else if (item.value !== undefined) {
      return String(item.value);
    }
    return null;
  },

  /* ============= Rendering ============== */

  // get Input Component from object
  _createInput: function (item: FormItem): ReactElement<any> {
    const placeholder =
      item.placeholder === true ? locale[item.ref] : item.placeholder;
    let properties = {
        "form-group": true,
        [item.ref]: true,
        placeholder: !!placeholder,
      },
      inputProperties = {
        highlight: item.highlight,
      },
      content: ReactElement<any> | null = null;

    // differentiate rendered from non-rendered items
    item.rendered = true;

    // if form is disabled just display some divs
    if (this.state.formDisabled) {
      properties.disabled = true;
      content = <div className="fakeInput">{this.getDisplay(item)}</div>;

      // otherwise create real form elements
    } else {
      // create select element
      if (item.options) {
        let options = [
          { value: "", display: locale.pleaseChoose },
          ...(item.options as OptionsLongForm),
        ];
        content = (
          <select
            ref={item.ref}
            name={item.ref}
            className={classnames(inputProperties)}
            onChange={this._handleChange.bind(this, item)}
            value={String(item.value)}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.display}
              </option>
            ))}
          </select>
        );
        properties.requiredSelect = !item.optional && item.value === "";

        // create textarea
      } else if (isTextAreaItem(item)) {
        content = (
          <textarea
            rows={item.rows}
            ref={item.ref}
            name={item.ref}
            className={classnames(inputProperties)}
            onChange={this._handleChange.bind(this, item)}
            value={item.value}
            placeholder={placeholder || ""}
          />
        );
        properties.requiredArea = !item.optional && item.value === "";

        // create checkbox
      } else if (isCheckItem(item)) {
        content = (
          <input
            type="checkbox"
            ref={item.ref}
            name={item.ref}
            checked={item.value}
            placeholder={placeholder || ""}
            onChange={this._handleChange.bind(this, item)}
            className={classnames(inputProperties)}
          />
        );
        properties.requiredCheck = !item.optional && !item.value;

        // create normal input
      } else {
        content = (
          <label>
            {placeholder || ""}
            <input
              type={item.type || "text"}
              ref={item.ref}
              name={item.ref}
              className={classnames(inputProperties)}
              onChange={this._handleChange.bind(this, item)}
              value={item.value}
            />
          </label>
        );
        properties.requiredInput = !item.optional && item.value === "";
      }
    }

    /* input group addon */
    if (item.addon) {
      content = (
        <div className="input-group">
          {content}
          <span className="input-group-addon">{item.addon}</span>
        </div>
      );
    }

    // generate label
    const labelText = item.label || locale[item.ref];
    let label = item.markdownLabel ? (
      <Markdown markdown={labelText} />
    ) : (
      labelText
    );

    // wrap label with tooltip
    if (item.tooltip) {
      label = (
        <span>
          {label}
          <Tooltip content={item.tooltip} />
        </span>
      );
    }

    /* label wrapper */
    if (isCheckItem(item)) {
      content = (
        <label className="checkLabel">
          {content}
          {label}
        </label>
      );
    } else if (!item.placeholder) {
      content = (
        <label>
          {label}: {content}
        </label>
      );
    }

    return (
      <div key={item.ref} className={classnames(properties)}>
        {content}
      </div>
    );
  },

  // generate all form components
  getFormComponents: function (options: { exclude?: string[] }) {
    let inputs: ReactElement<any>[] = [];
    options = options || {};
    this.state.form.forEach((item: FormItem) => {
      if (!options.exclude || options.exclude.indexOf(item.ref) == -1)
        inputs.push(this._createInput(item));
    });
    return inputs;
  },

  // generate single form component
  getFormComponent: function (ref: string) {
    let item = this.getItem(ref);
    if (item) {
      return this._createInput(item);
    } else {
      return "";
    }
  },
};
